import { ReactNode } from 'react'
import { Icon, IconName } from '@/components/Icon'

type SelectionIndicator = 'check' | 'checkbox'

interface ListItemProps {
	label: string
	secondaryLabel?: string
	icon?: ReactNode
	iconLeft?: IconName
	iconRight?: IconName
	selectionIndicator?: SelectionIndicator
	isSelected?: boolean
	isDisabled?: boolean
	onToggle?: () => void
	onFocus?: () => void
	tabIndex?: number
	children?: ReactNode
	className?: string
}

function CheckboxIndicator ({ checked }: { checked: boolean }) {
	return (
		<span
			className={[
				'spaces:inline-flex spaces:items-center spaces:justify-center spaces:p-4 spaces:rounded-8 spaces:border-2 spaces:text-brand-on-base spaces:transition-colors spaces:shrink-0',
				checked
					? 'spaces:bg-brand-base spaces:border-transparent'
					: 'spaces:bg-canvas-base spaces:border-canvas-1'
			].join(' ')}
		>
			<Icon
				name='Check'
				size={16}
				className={['spaces:shrink-0 spaces:transition-opacity', checked ? '' : 'spaces:opacity-0']
					.filter(Boolean)
					.join(' ')}
			/>
		</span>
	)
}

export function ListItem ({
	label,
	secondaryLabel,
	icon,
	iconLeft,
	iconRight,
	selectionIndicator = 'check',
	isSelected = false,
	isDisabled = false,
	onToggle,
	onFocus,
	tabIndex,
	className = ''
}: ListItemProps) {
	const active = isSelected
	const isOff = isDisabled

	const useCheckbox = selectionIndicator === 'checkbox'
	const useCheck = selectionIndicator === 'check'

	return (
		<button
			type='button'
			role='option'
			aria-selected={active}
			disabled={isOff}
			tabIndex={tabIndex}
			onClick={onToggle}
			onFocus={onFocus}
			className={[
				'spaces-list-item spaces:flex spaces:items-center spaces:gap-4 spaces:w-full spaces:pl-8 spaces:pr-12 spaces:py-8 spaces:text-left spaces:rounded-8 spaces:font-default spaces:transition-colors spaces:border-0 spaces:outline-none',
				active ? 'spaces-selected' : '',
				isOff ? 'spaces-disabled' : '',
				isOff ? 'spaces:hover:bg-canvas-base' : 'spaces:hover:bg-canvas-4',
				'spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
				isOff ? 'spaces:opacity-disabled spaces:cursor-not-allowed' : 'spaces:cursor-pointer',
				className
			]
				.filter(Boolean)
				.join(' ')}
		>
			{useCheckbox && <CheckboxIndicator checked={active} />}
			<div className='spaces:flex spaces:flex-1 spaces:items-center spaces:gap-8 spaces:min-w-0 spaces:pl-4'>
				{useCheck && (
					<Icon
						name='Check'
						size={16}
						className={[
							'spaces:shrink-0 spaces:text-brand-content spaces:transition-opacity',
							active ? '' : 'spaces:opacity-0'
						]
							.filter(Boolean)
							.join(' ')}
					/>
				)}
				{icon && (
					<span
						className={['spaces:shrink-0', active ? 'spaces:text-brand-content' : ''].join(' ')}
					>
						{icon}
					</span>
				)}
				{iconLeft && (
					<Icon
						name={iconLeft}
						size={16}
						className={['spaces:shrink-0', active ? 'spaces:text-brand-content' : ''].join(' ')}
					/>
				)}
				<span
					className={[
						'spaces:flex-1 spaces:min-w-0 spaces:text-16/24',
						active ? 'spaces:text-brand-content' : 'spaces:text-content-2'
					].join(' ')}
				>
					{label}
				</span>
				{secondaryLabel && (
					<span className='spaces:text-12/16 spaces:text-content-3 spaces:shrink-0 spaces:whitespace-nowrap'>
						{secondaryLabel}
					</span>
				)}
			</div>
		</button>
	)
}
